/**
 * Utility functions for generating secure random strings
 * Uses crypto.getRandomValues() for cryptographically secure random number generation
 */

/**
 * Generate a cryptographically secure random string
 * @param length - Length of the random string (default: 9)
 * @returns A random string containing lowercase letters and numbers
 * @throws Error if crypto.getRandomValues() is not available
 */
export function generateSecureRandomString(length: number = 9): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const array = new Uint8Array(length);
  
  // Use crypto.getRandomValues() for cryptographically secure random numbers
  // crypto.getRandomValues() is available in all modern browsers and Node.js environments
  // No fallback to Math.random() to maintain cryptographic security
  // Check both global crypto and window.crypto for maximum compatibility
  let cryptoObj: Crypto | null = null;
  
  if (typeof crypto !== 'undefined' && crypto && typeof crypto.getRandomValues === 'function') {
    cryptoObj = crypto;
  } else if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.getRandomValues === 'function') {
    cryptoObj = window.crypto;
  }
  
  if (!cryptoObj) {
    throw new Error('crypto.getRandomValues() is not available in this environment. This function requires a secure random number generator.');
  }
  
  cryptoObj.getRandomValues(array);
  
  return Array.from(array, x => chars[x % chars.length]).join('');
}

